/**
 * Generated by scripts/generate-drizzle-schema.mjs. Do not edit by hand.
 * This product's inputs: scripts/drizzle-schema.config.mjs.
 * Regenerate: bash scripts/client-schema-fresh.sh (data-model-change skill).
 */

import { DrizzleAppSchema, type PowerSyncSQLiteDatabase } from "@powersync/drizzle-driver";
import { index, integer, real, sqliteTable, text } from "drizzle-orm/sqlite-core";
import type { InferInsertModel, InferSelectModel } from "drizzle-orm";

export const activity_recent = sqliteTable(
  "activity_recent",
  {
    id: text("id").primaryKey(),
    scope_id: text("scope_id"),
    list_id: text("list_id"),
    entity_type: text("entity_type"),
    entity_id: text("entity_id"),
    action: text("action"),
    actor_user_id: text("actor_user_id"),
    actor_name: text("actor_name"),
    actor_device_id: text("actor_device_id"),
    platform: text("platform"),
    server_ts: text("server_ts"),
    client_ts: text("client_ts"),
    patch: text("patch"),
    batch_id: text("batch_id"),
    reverts_activity_id: text("reverts_activity_id"),
  },
  (table) => [
    /** Workspace feed ordered by server_ts desc */
    index("activity_recent_by_scope").on(table.scope_id, table.server_ts),
    /** Entity history ("History" sheet) */
    index("activity_recent_by_entity").on(table.entity_type, table.entity_id, table.server_ts),
  ],
);

// ⚡ Autonomous suggestions detected by local AI.
export const ai_suggestion = sqliteTable(
  "ai_suggestion",
  {
    id: text("id").primaryKey(),
    user_id: text("user_id"),
    start_time: text("start_time"),
    end_time: text("end_time"),
    title: text("title"),
    detected_apps: text("detected_apps"),
    status: text("status"),
    assigned_time_entry_id: text("assigned_time_entry_id"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
    deleted_at: text("deleted_at"),
  },
  (table) => [
    /** Filter pending floating AI suggestions */
    index("ai_suggestion_by_user_status").on(table.user_id, table.status),
  ],
);

// ⚡ Log of applications used by user.
export const application_log = sqliteTable(
  "application_log",
  {
    id: text("id").primaryKey(),
    user_id: text("user_id"),
    application_name: text("application_name"),
    window_title: text("window_title"),
    duration_seconds: integer("duration_seconds"),
    start_time: text("start_time"),
    assigned_time_entry_id: text("assigned_time_entry_id"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
    deleted_at: text("deleted_at"),
  },
  (table) => [
    /** Application log filtered by user */
    index("application_log_by_user").on(table.user_id),
  ],
);

// ⚡ Mapping of categories and projects suggested by AI.
export const category_mapping = sqliteTable(
  "category_mapping",
  {
    id: text("id").primaryKey(),
    user_id: text("user_id"),
    pattern: text("pattern"),
    project_id: text("project_id"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
    deleted_at: text("deleted_at"),
  },
  (table) => [
    /** Category mapping filtered by user */
    index("category_mapping_by_user").on(table.user_id),
  ],
);

// ⚡ Team collaborative activity events.
export const feed_event = sqliteTable(
  "feed_event",
  {
    id: text("id").primaryKey(),
    workspace_id: text("workspace_id"),
    user_id: text("user_id"),
    event_type: text("event_type"),
    event_data: text("event_data"),
    created_at: text("created_at"),
    deleted_at: text("deleted_at"),
  },
  (table) => [
    /** Order feed by descending date in team panel */
    index("feed_event_by_workspace_created").on(table.workspace_id, table.created_at),
  ],
);

// ⚡ Concentration and Deep Work sessions.
export const focus_session = sqliteTable(
  "focus_session",
  {
    id: text("id").primaryKey(),
    user_id: text("user_id"),
    project_id: text("project_id"),
    task_id: text("task_id"),
    session_type: text("session_type"),
    status: text("status"),
    start_time: text("start_time"),
    end_time: text("end_time"),
    current_segment: integer("current_segment"),
    total_segments: integer("total_segments"),
    energy_level_start: integer("energy_level_start"),
    energy_level_end: integer("energy_level_end"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
    deleted_at: text("deleted_at"),
  },
  (table) => [
    /** Used for pomodoro history and energy history */
    index("focus_session_by_user").on(table.user_id),
  ],
);

export const font_family = sqliteTable("font_family", {
  id: text("id").primaryKey(),
  family: text("family"),
  name: text("name"),
  category: text("category"),
  is_active: integer("is_active"),
  sort_order: integer("sort_order"),
  created_at: text("created_at"),
  updated_at: text("updated_at"),
});

export const font_variant = sqliteTable("font_variant", {
  id: text("id").primaryKey(),
  font_family_id: text("font_family_id"),
  weight: text("weight"),
  url: text("url"),
  created_at: text("created_at"),
  attachment_id: text("attachment_id"),
});

// ⚡ Pomodoro configuration per user.
export const pomodoro_config = sqliteTable(
  "pomodoro_config",
  {
    id: text("id").primaryKey(),
    user_id: text("user_id"),
    focus_duration: integer("focus_duration"),
    short_break_duration: integer("short_break_duration"),
    long_break_duration: integer("long_break_duration"),
    long_break_interval: integer("long_break_interval"),
    daily_goal: integer("daily_goal"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
    deleted_at: text("deleted_at"),
  },
  (table) => [
    /** Pomodoro configuration filtered by user */
    index("pomodoro_config_by_user").on(table.user_id),
  ],
);

// ⚡ Workspace projects.
export const project = sqliteTable(
  "project",
  {
    id: text("id").primaryKey(),
    workspace_id: text("workspace_id"),
    name: text("name"),
    code: text("code"),
    color: text("color"),
    status: text("status"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
    deleted_at: text("deleted_at"),
    hourly_rate: real("hourly_rate"),
    currency: text("currency"),
    billable: integer("billable"),
    current_version_id: text("current_version_id"),
    version_number: integer("version_number"),
  },
  (table) => [
    /** Filter projects by workspace */
    index("project_by_workspace").on(table.workspace_id),
    /** Used for selection grid by project status */
    index("project_by_status").on(table.status),
  ],
);

export const project_member = sqliteTable("project_member", {
  id: text("id").primaryKey(),
  project_id: text("project_id"),
  user_id: text("user_id"),
  created_at: text("created_at"),
  deleted_at: text("deleted_at"),
});

export const project_version = sqliteTable(
  "project_version",
  {
    id: text("id").primaryKey(),
    project_id: text("project_id"),
    workspace_id: text("workspace_id"),
    version_number: integer("version_number"),
    name: text("name"),
    code: text("code"),
    color: text("color"),
    status: text("status"),
    hourly_rate: real("hourly_rate"),
    currency: text("currency"),
    billable: integer("billable"),
    text: text("text"),
    client_ts: text("client_ts"),
    server_ts: text("server_ts"),
  },
  (table) => [
    /** WHERE project_id = ? ORDER BY id DESC in getProjectVersionsQuery */
    index("project_version_by_project").on(table.project_id, table.id),
  ],
);

// ⚡ Quick peer reactions to events.
export const reaction = sqliteTable(
  "reaction",
  {
    id: text("id").primaryKey(),
    feed_event_id: text("feed_event_id"),
    user_id: text("user_id"),
    emoji: text("emoji"),
    created_at: text("created_at"),
    deleted_at: text("deleted_at"),
  },
  (table) => [
    /** Group reactions associated with feed events */
    index("reaction_by_feed_event").on(table.feed_event_id),
  ],
);

// Active user sessions — small dataset, no indexes.
export const session = sqliteTable("session", {
  id: text("id").primaryKey(),
});

export const tag = sqliteTable("tag", {
  id: text("id").primaryKey(),
  workspace_id: text("workspace_id"),
  name: text("name"),
  color: text("color"),
  created_at: text("created_at"),
  updated_at: text("updated_at"),
  deleted_at: text("deleted_at"),
});

// ⚡ Tasks associated with projects.
export const task = sqliteTable(
  "task",
  {
    id: text("id").primaryKey(),
    project_id: text("project_id"),
    name: text("name"),
    completed: integer("completed"),
    completed_at: text("completed_at"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
    deleted_at: text("deleted_at"),
  },
  (table) => [
    /** Filter active project tasks */
    index("task_by_project").on(table.project_id, table.deleted_at),
  ],
);

// Small global dataset (~20 themes) — indexes not needed.
export const themes = sqliteTable("themes", {
  id: text("id").primaryKey(),
  name: text("name"),
  background: text("background"),
  surface: text("surface"),
  accent: text("accent"),
  glow_colors: text("glow_colors"),
  created_at: text("created_at"),
  updated_at: text("updated_at"),
  is_dark: integer("is_dark"),
  foreground: text("foreground"),
  field_background: text("field_background"),
  danger: text("danger"),
  success: text("success"),
  warning: text("warning"),
  muted: text("muted"),
  pattern_type: text("pattern_type"),
  pattern_opacity: integer("pattern_opacity"),
  pair_id: text("pair_id"),
  font_normal: text("font_normal"),
  font_medium: text("font_medium"),
  font_semibold: text("font_semibold"),
  font_bold: text("font_bold"),
  radius: text("radius"),
  surface_foreground: text("surface_foreground"),
  surface_secondary: text("surface_secondary"),
  surface_secondary_foreground: text("surface_secondary_foreground"),
  surface_tertiary: text("surface_tertiary"),
  surface_tertiary_foreground: text("surface_tertiary_foreground"),
  overlay: text("overlay"),
  overlay_foreground: text("overlay_foreground"),
  default: text("default"),
  default_foreground: text("default_foreground"),
  accent_foreground: text("accent_foreground"),
  field_foreground: text("field_foreground"),
  field_placeholder: text("field_placeholder"),
  field_border: text("field_border"),
  danger_foreground: text("danger_foreground"),
  success_foreground: text("success_foreground"),
  warning_foreground: text("warning_foreground"),
  segment: text("segment"),
  segment_foreground: text("segment_foreground"),
  border: text("border"),
  focus: text("focus"),
  link: text("link"),
  surface_shadow: text("surface_shadow"),
  overlay_shadow: text("overlay_shadow"),
  field_shadow: text("field_shadow"),
  muted_foreground: text("muted_foreground"),
  divider: text("divider"),
  glass_background: text("glass_background"),
  glass_border: text("glass_border"),
  glass_highlight: text("glass_highlight"),
  is_active: integer("is_active"),
  sort_order: integer("sort_order"),
});

// ⚡ Time worked records by project and task.
export const time_entry = sqliteTable(
  "time_entry",
  {
    id: text("id").primaryKey(),
    user_id: text("user_id"),
    project_id: text("project_id"),
    task_id: text("task_id"),
    description: text("description"),
    start_time: text("start_time"),
    end_time: text("end_time"),
    is_running: integer("is_running"),
    source: text("source"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
    deleted_at: text("deleted_at"),
    project_version_id: text("project_version_id"),
  },
  (table) => [
    /** Used in active timer widget */
    index("time_entry_by_timer").on(table.user_id, table.is_running),
    /** Used in timeline ordered desc */
    index("time_entry_by_timeline").on(table.user_id, table.start_time, table.end_time),
  ],
);

export const time_entry_tag = sqliteTable("time_entry_tag", {
  id: text("id").primaryKey(),
  time_entry_id: text("time_entry_id"),
  tag_id: text("tag_id"),
  created_at: text("created_at"),
});

// Only 1 row per user — indexes not needed.
export const user = sqliteTable("user", {
  id: text("id").primaryKey(),
  name: text("name"),
  email: text("email"),
  email_verified: integer("email_verified"),
  image: text("image"),
  created_at: text("created_at"),
  updated_at: text("updated_at"),
  locale: text("locale"),
  global_theme_id: text("global_theme_id"),
  is_blur: integer("is_blur"),
  font_family: text("font_family"),
  follow_device_theme: integer("follow_device_theme"),
  role: text("role"),
  current_status: text("current_status"),
});

// ⚡ Collaborative workspaces.
export const workspace = sqliteTable("workspace", {
  id: text("id").primaryKey(),
  name: text("name"),
  slug: text("slug"),
  created_at: text("created_at"),
  updated_at: text("updated_at"),
  deleted_at: text("deleted_at"),
});

// ⚡ Members and roles within workspace.
export const workspace_member = sqliteTable("workspace_member", {
  id: text("id").primaryKey(),
  workspace_id: text("workspace_id"),
  user_id: text("user_id"),
  role: text("role"),
  created_at: text("created_at"),
  updated_at: text("updated_at"),
  deleted_at: text("deleted_at"),
});

export const drizzleSchema = {
  activity_recent,
  ai_suggestion,
  application_log,
  category_mapping,
  feed_event,
  focus_session,
  font_family,
  font_variant,
  pomodoro_config,
  project,
  project_member,
  project_version,
  reaction,
  session,
  tag,
  task,
  themes,
  time_entry,
  time_entry_tag,
  user,
  workspace,
  workspace_member,
};

export const AppSchema = new DrizzleAppSchema(drizzleSchema);

export type Database = (typeof AppSchema)["types"];

/**
 * Type helper for the Drizzle database instance wrapped with PowerSync.
 */
export type PowerSyncDrizzleDB = PowerSyncSQLiteDatabase<typeof drizzleSchema>;

export const USER_DATA_TABLES = [
  "ai_suggestion",
  "application_log",
  "category_mapping",
  "feed_event",
  "focus_session",
  "pomodoro_config",
  "project_member",
  "reaction",
  "time_entry",
  "workspace_member",
] as const;

export type ActivityRecent = InferSelectModel<typeof activity_recent>;
export type ActivityRecentInsert = InferInsertModel<typeof activity_recent>;
export type ActivityRecentRecord = ActivityRecent;
export type AiSuggestion = InferSelectModel<typeof ai_suggestion>;
export type AiSuggestionInsert = InferInsertModel<typeof ai_suggestion>;
export type AiSuggestionRecord = AiSuggestion;
export type ApplicationLog = InferSelectModel<typeof application_log>;
export type ApplicationLogInsert = InferInsertModel<typeof application_log>;
export type ApplicationLogRecord = ApplicationLog;
export type CategoryMapping = InferSelectModel<typeof category_mapping>;
export type CategoryMappingInsert = InferInsertModel<typeof category_mapping>;
export type CategoryMappingRecord = CategoryMapping;
export type FeedEvent = InferSelectModel<typeof feed_event>;
export type FeedEventInsert = InferInsertModel<typeof feed_event>;
export type FeedEventRecord = FeedEvent;
export type FocusSession = InferSelectModel<typeof focus_session>;
export type FocusSessionInsert = InferInsertModel<typeof focus_session>;
export type FocusSessionRecord = FocusSession;
export type FontFamily = InferSelectModel<typeof font_family>;
export type FontFamilyInsert = InferInsertModel<typeof font_family>;
export type FontFamilyRecord = FontFamily;
export type FontVariant = InferSelectModel<typeof font_variant>;
export type FontVariantInsert = InferInsertModel<typeof font_variant>;
export type FontVariantRecord = FontVariant;
export type PomodoroConfig = InferSelectModel<typeof pomodoro_config>;
export type PomodoroConfigInsert = InferInsertModel<typeof pomodoro_config>;
export type PomodoroConfigRecord = PomodoroConfig;
export type Project = InferSelectModel<typeof project>;
export type ProjectInsert = InferInsertModel<typeof project>;
export type ProjectRecord = Project;
export type ProjectMember = InferSelectModel<typeof project_member>;
export type ProjectMemberInsert = InferInsertModel<typeof project_member>;
export type ProjectMemberRecord = ProjectMember;
export type ProjectVersion = InferSelectModel<typeof project_version>;
export type ProjectVersionInsert = InferInsertModel<typeof project_version>;
export type ProjectVersionRecord = ProjectVersion;
export type Reaction = InferSelectModel<typeof reaction>;
export type ReactionInsert = InferInsertModel<typeof reaction>;
export type ReactionRecord = Reaction;
export type Session = InferSelectModel<typeof session>;
export type SessionInsert = InferInsertModel<typeof session>;
export type SessionRecord = Session;
export type Tag = InferSelectModel<typeof tag>;
export type TagInsert = InferInsertModel<typeof tag>;
export type TagRecord = Tag;
export type Task = InferSelectModel<typeof task>;
export type TaskInsert = InferInsertModel<typeof task>;
export type TaskRecord = Task;
export type Themes = InferSelectModel<typeof themes>;
export type ThemesInsert = InferInsertModel<typeof themes>;
export type ThemesRecord = Themes;
export type TimeEntry = InferSelectModel<typeof time_entry>;
export type TimeEntryInsert = InferInsertModel<typeof time_entry>;
export type TimeEntryRecord = TimeEntry;
export type TimeEntryTag = InferSelectModel<typeof time_entry_tag>;
export type TimeEntryTagInsert = InferInsertModel<typeof time_entry_tag>;
export type TimeEntryTagRecord = TimeEntryTag;
export type User = InferSelectModel<typeof user>;
export type UserInsert = InferInsertModel<typeof user>;
export type UserRecord = User;
export type Workspace = InferSelectModel<typeof workspace>;
export type WorkspaceInsert = InferInsertModel<typeof workspace>;
export type WorkspaceRecord = Workspace;
export type WorkspaceMember = InferSelectModel<typeof workspace_member>;
export type WorkspaceMemberInsert = InferInsertModel<typeof workspace_member>;
export type WorkspaceMemberRecord = WorkspaceMember;
